import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  FilmSlate, WarningDiamond, X, FunnelSimple, ImageSquare, VideoCamera, Clock, Trash, TrashSimple,
} from "@phosphor-icons/react";

const SEV = {
  critical: { label: "Critical", color: "#FF3B30", bg: "rgba(255,59,48,0.12)" },
  high: { label: "High", color: "#FF9F0A", bg: "rgba(255,159,10,0.12)" },
  medium: { label: "Medium", color: "#FFD60A", bg: "rgba(255,214,10,0.10)" },
  low: { label: "Low", color: "#64D2FF", bg: "rgba(100,210,255,0.10)" },
};
const TYPES = ["Theft", "Vandalism", "Fight", "Aggression", "Intoxication", "Productivity"];

function mediaUrl(path) {
  if (!path) return null;
  const token = localStorage.getItem("sv_token");
  return `${API}/events/media/${path}?auth=${encodeURIComponent(token || "")}`;
}

function fmt(ts) {
  try { return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [cams, setCams] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ severity: "", type: "", camera_id: "" });

  useEffect(() => { api.get("/cameras").then((r) => setCams(r.data)).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (f.severity) params.severity = f.severity;
    if (f.type) params.type = f.type;
    if (f.camera_id) params.camera_id = f.camera_id;
    api.get("/events", { params })
      .then((r) => { setEvents(r.data.events || []); setRetention(r.data.retention_days || 30); })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [f]);

  const removeOne = (e, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Remove this ${e.type} event? This permanently deletes it.`)) return;
    api.delete(`/events/${e.id}`)
      .then(() => { setEvents((list) => list.filter((x) => x.id !== e.id)); if (sel?.id === e.id) setSel(null); toast.success("Event removed"); })
      .catch(() => toast.error("Could not remove event"));
  };
  const clearAll = () => {
    const filtered = f.severity || f.type || f.camera_id;
    if (!window.confirm(filtered
      ? "Clear all events matching the current filters? This permanently deletes them."
      : "Clear ALL events? This permanently deletes every event shown.")) return;
    const params = {};
    if (f.severity) params.severity = f.severity;
    if (f.type) params.type = f.type;
    if (f.camera_id) params.camera_id = f.camera_id;
    api.post("/events/clear", null, { params })
      .then((r) => { toast.success(`Cleared ${r.data.removed} event(s)`); load(); })
      .catch(() => toast.error("Could not clear events"));
  };

  const stats = useMemo(() => {
    const withClip = events.filter((e) => e.clip_path).length;
    return { total: events.length, clips: withClip };
  }, [events]);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-display font-black text-3xl sm:text-4xl tracking-tight text-white">
            <FilmSlate size={34} weight="fill" className="text-[#0A84FF]" /> Event History
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Every AI-flagged event is saved with a snapshot; high &amp; critical events also keep a short clip. Kept for {retention} days.
          </p>
        </div>
        <div className="flex items-center gap-4">
        <div className="flex gap-4 font-mono-data text-sm">
          <span className="text-[#A1A1AA]" data-testid="events-total">{stats.total} events</span>
          <span className="text-[#0A84FF]" data-testid="events-clips">{stats.clips} with clip</span>
        </div>
        {events.length > 0 && (
          <button data-testid="events-clear-all" onClick={clearAll}
            className="flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 transition-colors">
            <TrashSimple size={14} weight="bold" /> {f.severity || f.type || f.camera_id ? "Clear Filtered" : "Clear All"}
          </button>
        )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-[#121214] border border-white/10 p-3">
        <FunnelSimple size={18} className="text-[#71717A]" />
        <select data-testid="events-filter-severity" value={f.severity} onChange={(e) => setF((p) => ({ ...p, severity: e.target.value }))}
          className="bg-[#1A1A1E] border border-white/10 text-sm px-3 py-2 text-white outline-none focus:border-[#0A84FF]">
          <option value="">All severities</option>
          {Object.keys(SEV).map((s) => <option key={s} value={s}>{SEV[s].label}</option>)}
        </select>
        <select data-testid="events-filter-type" value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value }))}
          className="bg-[#1A1A1E] border border-white/10 text-sm px-3 py-2 text-white outline-none focus:border-[#0A84FF]">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="events-filter-camera" value={f.camera_id} onChange={(e) => setF((p) => ({ ...p, camera_id: e.target.value }))}
          className="bg-[#1A1A1E] border border-white/10 text-sm px-3 py-2 text-white outline-none focus:border-[#0A84FF]">
          <option value="">All cameras</option>
          {cams.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(f.severity || f.type || f.camera_id) && (
          <button data-testid="events-clear-filters" onClick={() => setF({ severity: "", type: "", camera_id: "" })}
            className="text-xs text-[#71717A] hover:text-white underline">Clear</button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-[#71717A] font-mono-data text-sm py-16 text-center">Loading events…</p>
      ) : events.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-white/10" data-testid="events-empty">
          <ImageSquare size={40} weight="thin" className="mx-auto text-[#3f3f46]" />
          <p className="text-[#A1A1AA] mt-3">No events stored yet.</p>
          <p className="text-[#71717A] text-sm mt-1">When the AI flags theft, vandalism, a fight or low productivity, it will appear here with a snapshot.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="events-grid">
          {events.map((e) => {
            const sev = SEV[e.severity] || SEV.low;
            return (
              <button key={e.id} data-testid={`event-card-${e.id}`} onClick={() => setSel(e)}
                className="group text-left bg-[#121214] border border-white/10 hover:border-[#0A84FF]/50 overflow-hidden transition-colors">
                <div className="relative aspect-video bg-black">
                  {e.snapshot_path ? (
                    <img src={mediaUrl(e.snapshot_path)} alt={e.type} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#3f3f46]"><ImageSquare size={26} weight="thin" /></div>
                  )}
                  <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data font-bold uppercase"
                    style={{ color: sev.color, background: sev.bg }}>
                    <WarningDiamond size={11} weight="fill" /> {e.type}
                  </span>
                  {e.clip_path && (
                    <span className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 text-white text-[10px] font-mono-data px-1.5 py-0.5" data-testid={`event-hasclip-${e.id}`}>
                      <VideoCamera size={11} weight="fill" /> CLIP
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between text-[11px] font-mono-data text-[#71717A]">
                    <span className="truncate">{e.camera}</span>
                    <span className="flex items-center gap-1 shrink-0"><Clock size={11} /> {fmt(e.ts)}</span>
                  </div>
                  <p className="text-xs text-[#D4D4D8] mt-1.5 line-clamp-2 leading-relaxed">{e.message}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {sel && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSel(null)} data-testid="event-modal">
          <div className="bg-[#0F0F12] border border-white/15 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="flex items-center gap-2 font-display font-bold uppercase tracking-wide"
                style={{ color: (SEV[sel.severity] || SEV.low).color }}>
                <WarningDiamond size={18} weight="fill" /> {sel.type} · {(SEV[sel.severity] || SEV.low).label}
              </span>
              <button data-testid="event-modal-close" onClick={() => setSel(null)} className="text-[#71717A] hover:text-white"><X size={22} /></button>
            </div>
            <div className="p-4 space-y-4">
              {sel.clip_path ? (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-[#71717A] mb-1.5 flex items-center gap-1.5"><VideoCamera size={13} /> Event clip</p>
                  <img src={mediaUrl(sel.clip_path)} alt="event clip" className="w-full border border-white/10" data-testid="event-modal-clip" />
                </div>
              ) : sel.snapshot_path ? (
                <img src={mediaUrl(sel.snapshot_path)} alt="event snapshot" className="w-full border border-white/10" data-testid="event-modal-snapshot" />
              ) : (
                <div className="aspect-video bg-black flex items-center justify-center text-[#3f3f46]"><ImageSquare size={32} weight="thin" /></div>
              )}
              {sel.clip_path && sel.snapshot_path && (
                <img src={mediaUrl(sel.snapshot_path)} alt="event snapshot" className="w-full border border-white/10" />
              )}
              <div className="font-mono-data text-sm space-y-1.5 text-[#A1A1AA]">
                <div className="flex justify-between"><span>Camera</span><span className="text-white">{sel.camera}</span></div>
                <div className="flex justify-between"><span>Time</span><span className="text-white">{fmt(sel.ts)}</span></div>
                <div className="flex justify-between"><span>Status</span><span className="text-white capitalize">{sel.status}</span></div>
              </div>
              <p className="text-sm text-[#D4D4D8] leading-relaxed border-t border-white/10 pt-3">{sel.message}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
